import * as React from 'react'
import { withRequiredPermission } from '@/components/auth/with-required-permission'
import { PERMISSIONS } from '@/hooks/use-perm'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { HelpCircle } from 'lucide-react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'

import {
    usePaymentFlow,
    useUpdatePaymentFlow,
    PaymentFlowDetails,
} from '@/lib/api/paymentFlows'
import { ErrorDisplay } from '@/components/ui/error-display'
import { parseTRPCError } from '@/lib/utils/trpc-error-utils'
import { Button } from '@/components/ui/button'
import { Card, CardHeader, CardTitle, CardAction, CardContent } from '@/components/ui/card'
import { Switch } from '@/components/ui/switch'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Label } from '@/components/ui/label'
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip'
import { Skeleton } from '@/components/ui/skeleton'
import { PAYMENT_FLOW_SUB_NAVIGATION_IDS, PaymentFlowSubNavigation } from '../components/sub-menu-navigation'
import { BackButton } from '@/components/ui/BackButton'
import { CustomRetrySchedule, AdvancedOptions, generateRetryId } from './components'
import { LabelWithTooltip } from '@/components/ui/label-with-tooltip'
import { useOrgNavigate } from '@/hooks/use-app-navigate'

/**
 * Zod schema for retry attempt
 */
const retryAttemptSchema = z.object({
    id: z.string(),
    value: z.number().min(1, 'Value must be at least 1'),
    unit: z.enum(['days', 'weeks']),
    description: z.string().min(1, 'Description is required'),
})

/**
 * Zod schema for advanced options
 */
const advancedOptionsSchema = z.object({
    expiredCardNoRetries: z.boolean(),
    retryOnPayrollDays: z.boolean(),
    avoidWeekends: z.boolean(),
    velocityLimitWait24h: z.boolean(),
    timeoutRetryMinutes: z.boolean(),
    code51RetryPayroll: z.boolean(),
    code05Wait48h: z.boolean(),
})

/**
 * Zod schema for smart retry configuration
 */
const smartRetryConfigSchema = z.object({
    isAdvancedEnabled: z.boolean(),
    customRetries: z.array(retryAttemptSchema).min(1, 'At least one retry attempt is required'),
    advancedOptions: advancedOptionsSchema,
    afterLastRetry: z.enum(['cancel', 'pause']).optional(),
})

const smartRetryFormSchema = z.object({
    smartRetryEnabled: z.boolean(),
    config: smartRetryConfigSchema.nullable(),
}).refine(
    (data) => {
        if (data.smartRetryEnabled) {
            return data.config !== null
        }
        return true
    },
    {
        message: 'Config is required when smart retry is enabled',
        path: ['config'],
    }
)

export type SmartRetryFormData = z.infer<typeof smartRetryFormSchema>

/**
 * Loading skeleton for smart retry page
 */
const SmartRetrySkeleton = () => (
    <div className="">
        <div className="mb-4">
            <Skeleton className="h-9 w-full max-w-md" />
        </div>
        <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-4">
                <Skeleton className="size-10" />
                <div className="space-y-2">
                    <Skeleton className="h-8 w-64" />
                    <Skeleton className="h-4 w-96" />
                </div>
            </div>
        </div>
        <Skeleton className="h-[600px] w-full rounded-xl" />
    </div>
)

/**
 * Container component for smart retry page
 */
const SmartRetryPageInner = () => {
    const { flowId } = useParams()
    const { t } = useTranslation('paymentFlows')

    const {
        data: paymentFlow,
        isLoading,
        error,
    } = usePaymentFlow(flowId || '', !!flowId)

    if (!flowId) {
        return null
    }

    if (isLoading) {
        return <SmartRetrySkeleton />
    }

    if (error) {
        return <ErrorDisplay error={parseTRPCError(error)} />
    }

    if (!paymentFlow) {
        return (
            <div className="container mx-auto py-8">
                <p>{t('details.not_found')}</p>
            </div>
        )
    }

    return <SmartRetryContent paymentFlow={paymentFlow} />
}

/**
 * Default config values when enabling smart retry
 */
const getDefaultConfig = () => ({
    isAdvancedEnabled: false,
    customRetries: [
        { id: generateRetryId(), value: 1, unit: 'days' as const, description: '1 day later' },
        { id: generateRetryId(), value: 2, unit: 'days' as const, description: '2 days later' },
        { id: generateRetryId(), value: 4, unit: 'days' as const, description: '4 days later' },
        { id: generateRetryId(), value: 7, unit: 'days' as const, description: '7 days later' },
        { id: generateRetryId(), value: 14, unit: 'days' as const, description: '14 days later' },
    ],
    advancedOptions: {
        expiredCardNoRetries: true,
        retryOnPayrollDays: true,
        avoidWeekends: true,
        velocityLimitWait24h: true,
        timeoutRetryMinutes: true,
        code51RetryPayroll: true,
        code05Wait48h: true,
    },
})

/**
 * Form component for editing smart retry settings
 */
const SmartRetryContent = ({
    paymentFlow,
}: {
    paymentFlow: NonNullable<PaymentFlowDetails>
}) => {
    const { orgRoutes, navigate } = useOrgNavigate()
    const updatePaymentFlow = useUpdatePaymentFlow()
    const { t } = useTranslation(['paymentFlows', 'common'])

    const existingConfig = (paymentFlow as any).rebillSettings

    const methods = useForm<SmartRetryFormData>({
        resolver: zodResolver(smartRetryFormSchema),
        defaultValues: {
            smartRetryEnabled: existingConfig !== null,
            config: existingConfig || getDefaultConfig(),
        },
    })

    const {
        handleSubmit,
        control,
        watch,
        setValue,
        formState: { errors },
    } = methods

    const smartRetryEnabled = watch('smartRetryEnabled')
    const config = watch('config')

    const onSubmit = async (data: SmartRetryFormData) => {
        await updatePaymentFlow.mutateAsync({
            flowId: paymentFlow.id,
            data: {
                name: paymentFlow.name,
                pickProcessorStrategy: (paymentFlow.pickProcessorStrategy as 'weighted' | 'automatic') || 'weighted',
                fallbackMode: paymentFlow.fallbackMode ?? false,
                maxFallbackRetries: paymentFlow.maxFallbackRetries ?? 2,
                threeDsEnabled: paymentFlow.threeDsEnabled ?? false,
                stickyProcessorEnabled: paymentFlow.stickyProcessorEnabled ?? false,
                processorConfigs:
                    paymentFlow.processorConfigs?.map((config) => ({
                        processorId: config.processorId || '',
                        weight: config.weight ?? 0,
                        disabled: config.disabled ?? false,
                        nonStickable: config.nonStickable ?? false,
                    })) || [],
                fallbackProcessorConfigs:
                    paymentFlow.fallbackProcessorConfigs?.map((config, index) => ({
                        processorId: config.processorId || '',
                        orderIndex: config.orderIndex ?? index,
                    })) || [],
                rebillSettings: data.smartRetryEnabled && data.config ? data.config : undefined,
            },
        })

        navigate(orgRoutes.PAYMENT_FLOW_DETAILS(paymentFlow.id))
    }

    const handleCancel = () => {
        navigate(orgRoutes.PAYMENT_FLOW_DETAILS(paymentFlow.id))
    }

    const handleToggleSmartRetry = (checked: boolean) => {
        setValue('smartRetryEnabled', checked)
        if (checked && !config) {
            setValue('config', getDefaultConfig())
        } else if (!checked) {
            setValue('config', null)
        }
    }

    const isPending = updatePaymentFlow.isPending

    return (
        <div className="">
            <PaymentFlowSubNavigation activeId={PAYMENT_FLOW_SUB_NAVIGATION_IDS.SMART_RETRY} />
            <div className="flex items-center justify-between mb-8">
                <div className="flex items-center gap-4">
                    <BackButton to={orgRoutes.PAYMENT_FLOW_DETAILS(paymentFlow.id)} />
                    <div>
                        <h1 className="text-2xl font-bold sm:text-3xl">
                            {t('smartRetry.title')}
                        </h1>
                        <p className="text-muted-foreground text-sm">
                            {t('smartRetry.subtitle', { name: paymentFlow.name })}
                        </p>
                    </div>
                </div>
            </div>

            <form onSubmit={handleSubmit(onSubmit)}>
                <Card>
                    <CardHeader>
                        <div className="flex items-center gap-2">
                            <CardTitle>{t('smartRetry.card.title')}</CardTitle>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <HelpCircle className="size-4 cursor-help text-gray-400 hover:text-gray-600" />
                                </TooltipTrigger>
                                <TooltipContent>
                                    <p className="max-w-sm text-xs">
                                        {t('smartRetry.card.description')}
                                    </p>
                                </TooltipContent>
                            </Tooltip>
                        </div>
                        <CardAction>
                            <Controller
                                name="smartRetryEnabled"
                                control={control}
                                render={({ field: { value, onChange } }) => (
                                    <Switch checked={value} onCheckedChange={(checked) => {
                                        onChange(checked)
                                        handleToggleSmartRetry(checked)
                                    }} />
                                )}
                            />
                        </CardAction>
                    </CardHeader>

                    <CardContent>
                        <div className="mb-6">
                            <p className="text-sm text-gray-600">
                                {!smartRetryEnabled ? (
                                    <>
                                        <span className="font-medium text-gray-600">
                                            {t('smartRetry.status.disabled.label')}
                                        </span> {t('smartRetry.status.disabled.description')}
                                    </>
                                ) : (
                                    <>
                                        <span className="font-medium text-blue-600">
                                            {t('smartRetry.status.enabled.label')}
                                        </span> {t('smartRetry.status.enabled.description')}
                                    </>
                                )}
                            </p>
                        </div>

                        {smartRetryEnabled && config && (
                            <div className="space-y-6">
                                <div>
                                    <div className="mb-3 flex items-center justify-between">
                                        <LabelWithTooltip
                                            label={t('smartRetry.fields.advancedMode.label')}
                                            tooltip={t('smartRetry.fields.advancedMode.tooltip')}
                                        />
                                        <Controller
                                            name="config.isAdvancedEnabled"
                                            control={control}
                                            render={({ field: { value, onChange } }) => (
                                                <Switch checked={value} onCheckedChange={onChange} />
                                            )}
                                        />
                                    </div>
                                </div>

                                {config.isAdvancedEnabled && (
                                    <div className="space-y-6">
                                        <CustomRetrySchedule
                                            control={control}
                                            setValue={setValue}
                                            errors={errors}
                                            config={config}
                                        />
                                        <AdvancedOptions control={control} />
                                    </div>
                                )}
                                <div>
                                    <div className="mb-3">
                                        <LabelWithTooltip
                                            label={t('smartRetry.fields.afterLastRetry.label')}
                                            tooltip={t('smartRetry.fields.afterLastRetry.tooltip')}
                                        />
                                    </div>
                                    <Controller
                                        name="config.afterLastRetry"
                                        control={control}
                                        render={({ field: { value, onChange } }) => (
                                            <RadioGroup value={value ?? 'cancel'} onValueChange={onChange}>
                                                <div className="flex items-start gap-2">
                                                    <RadioGroupItem value="cancel" id="after-last-retry-cancel" className="mt-0.5" />
                                                    <Label htmlFor="after-last-retry-cancel" className="text-sm font-normal">
                                                        {t('smartRetry.fields.afterLastRetry.cancel')}
                                                    </Label>
                                                </div>
                                                <div className="flex items-start gap-2">
                                                    <RadioGroupItem value="pause" id="after-last-retry-pause" className="mt-0.5" />
                                                    <div className="space-y-1">
                                                        <Label htmlFor="after-last-retry-pause" className="text-sm font-normal">
                                                            {t('smartRetry.fields.afterLastRetry.pause')}
                                                        </Label>
                                                        <p className="text-xs text-muted-foreground">
                                                            {t('smartRetry.fields.afterLastRetry.pauseHint')}
                                                        </p>
                                                    </div>
                                                </div>
                                            </RadioGroup>
                                        )}
                                    />
                                </div>
                            </div>
                        )}

                        {!smartRetryEnabled && (
                            <p className="text-sm text-gray-500">
                                {t('smartRetry.toggleHint')}
                            </p>
                        )}
                    </CardContent>
                </Card>

                <div className="mt-6 flex justify-end gap-2">
                    <Button
                        type="button"
                        variant="outline"
                        onClick={handleCancel}
                        disabled={isPending}
                    >
                        {t('common:cancel')}
                    </Button>
                    <Button type="submit" disabled={isPending}>
                        {isPending
                            ? t('common:saving')
                            : t('common:save')}
                    </Button>
                </div>
            </form>
        </div>
    )
}

export const SmartRetryPage = withRequiredPermission(SmartRetryPageInner, PERMISSIONS.PROCESSORS_MANAGE)
export default SmartRetryPage

